// ABOUTME: Viewport observation for the reel overlays (SPEC-26 Phase 4/4b) — size, composition, video box.
// ABOUTME: One resize listener for the whole app, shared through a module-level store.

import { useSyncExternalStore, useMemo } from 'react';
import { computeVideoBox } from './videoBoxMath';
import { reelVideoFit } from './reelVideoFit';
import {
  subscribeDeviceProfile, getDeviceProfileSnapshot, getDeviceProfileServerSnapshot,
} from '../../utils/deviceProfile';

// Below this width the desktop composition cannot carry what it is made of — a 1138.67-authored-px
// rail and a 984-authored-px expanded panel — whatever shape the window is. Kept at the value the
// rest of the codebase already uses (App.js's `window.innerWidth < 768` checks) so the app doesn't
// end up with two disagreeing definitions.
export const MOBILE_MAX_WIDTH = 768;

/**
 * Which authored Figma composition applies to a viewport: 'desktop' | 'mobile'.
 *
 * **Width gated on device, as of Phase 6 (2026-08-14, client direction).** Phase 4b decided this on
 * aspect first (portrait shape → portrait frame, which sent portrait tablets to the mobile frame).
 * Phase 5 dropped that in favour of width alone, on the grounds that a tablet should get the
 * **desktop** composition outright — but width alone also means a desktop browser window dragged, or
 * zoomed, narrower than the rail's floor gets served the phone layout, which is not what "desktop
 * site" means to a guest who owns a mouse. That is the bug this phase closes.
 *
 * The rule is now: a narrow viewport only earns the mobile composition when the *device* is actually
 * a phone (`deviceProfile.mobileUA` — see `utils/deviceProfile.js`'s `detectMobileUA`). Everything
 * that reports as non-mobile — a laptop, a desktop monitor, an iPad (iPadOS spoofs as a Mac and so
 * never sets `mobileUA`) — gets the desktop composition at every width, including one dragged or
 * zoomed down to a sliver. A tablet still gets the desktop composition either way, so Phase 5's
 * outcome for tablets is unchanged; only the "no `mobileUA`" branch's floor moves from "never mobile
 * below 768" to "never mobile, full stop".
 *
 * `deviceProfile` is optional so the rule stays testable without a device-profile fixture — omitting
 * it (or passing one with `mobileUA` unset/false) is "not a phone", the same safe default as an
 * unmeasured viewport.
 *
 * Pure and exported so the rule can be tested directly, without a DOM, and so anything outside
 * React (a test helper, a future SSR pass) resolves it the same way the hooks do.
 */
export function resolveComposition({ width, height }, deviceProfile) {
  // Nothing measured yet (SSR, or the very first paint in a headless environment). Desktop is the
  // safe default: it is the composition every non-phone client gets, and picking mobile here would
  // flash the phone layout on every desktop load.
  if (!width || !height) return 'desktop';
  if (!deviceProfile || !deviceProfile.mobileUA) return 'desktop';
  return width < MOBILE_MAX_WIDTH ? 'mobile' : 'desktop';
}

// A module-level store rather than per-hook state, because these hooks compose: PointTagLayer alone
// calls useBreakpoint() and useVideoBox(), and each of those reads the viewport. Per-hook listeners
// would grow one pair per call site and let two copies of the same measurement disagree.
const SSR_SIZE = { width: 0, height: 0 };

/**
 * `documentElement.clientWidth/Height` in preference to `window.innerWidth/Height`, because every
 * surface these hooks position is `position: fixed; inset: 0` — and a fixed box is laid out against
 * the *layout* viewport, which is exactly what `clientWidth/Height` reports. `innerHeight` is the
 * visual viewport: on iOS Safari the two differ by the height of the collapsing URL bar, and on a
 * desktop browser with a classic scrollbar `innerWidth` is ~15px wider than the box actually is.
 * Both would put an overlay a few percent off the mark on exactly the browsers this phase targets.
 *
 * jsdom implements the client* properties as 0, so the `||` fallback is also the test path.
 */
function readViewport() {
  if (typeof window === 'undefined') return SSR_SIZE;
  const doc = typeof document !== 'undefined' ? document.documentElement : null;
  return {
    width: (doc && doc.clientWidth) || window.innerWidth || 0,
    height: (doc && doc.clientHeight) || window.innerHeight || 0,
  };
}

let snapshot = readViewport();
const listeners = new Set();
let teardown = null;

// Only replaces the snapshot when the size actually changed, so its identity is stable and
// useSyncExternalStore's Object.is check bails subscribers out of the re-render — a resize event
// that reports the same dimensions (they fire freely) costs nothing.
function handleViewportChange() {
  const next = readViewport();
  if (next.width === snapshot.width && next.height === snapshot.height) return;
  snapshot = next;
  listeners.forEach((listener) => listener());
}

/**
 * Four sources, because no single one fires everywhere:
 *  - `resize` — the baseline, every browser.
 *  - `orientationchange` — fires on iOS Safari before `resize` settles; deprecated but still the
 *    earliest signal there.
 *  - `screen.orientation` `change` — the standards-track replacement, on Chromium/Firefox.
 *  - `visualViewport` `resize` — the ONLY signal when an on-screen keyboard opens or the user
 *    pinch-zooms. We re-read the layout viewport rather than using the visual one's numbers, so
 *    this is a change trigger, not a measurement source.
 */
function subscribe(listener) {
  listeners.add(listener);
  if (!teardown && typeof window !== 'undefined') {
    const unbinds = [];
    const on = (target, type) => {
      if (!target || typeof target.addEventListener !== 'function') return;
      target.addEventListener(type, handleViewportChange);
      unbinds.push(() => target.removeEventListener(type, handleViewportChange));
    };
    on(window, 'resize');
    on(window, 'orientationchange');
    on(window.screen && window.screen.orientation, 'change');
    on(window.visualViewport, 'resize');
    teardown = () => unbinds.forEach((unbind) => unbind());
  }
  // Re-read on subscribe as well as on change: between this module being evaluated and the first
  // component mounting, the viewport can already differ (a rotate during load).
  handleViewportChange();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && teardown) {
      teardown();
      teardown = null;
    }
  };
}

function getSnapshot() {
  return snapshot;
}

function getServerSnapshot() {
  return SSR_SIZE;
}

/** Current viewport size, kept in sync on resize, rotation and visual-viewport changes. */
export function useViewportSize() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Which authored Figma composition applies: 'desktop' | 'mobile'.
 *
 * Reads both the live viewport and the live device profile — see `resolveComposition` for why the
 * device matters as of Phase 6. Subscribing to `useDeviceProfile()` here (rather than reading it
 * once) means plugging in a mouse or rotating a 2-in-1 can't leave a stale mobile-vs-desktop verdict
 * in place; the device signals that gate composition are exactly the ones that hook already tracks.
 */
export function useBreakpoint() {
  return resolveComposition(useViewportSize(), useDeviceProfile());
}

/** Reads better at call sites that are choosing a layout rather than a size. */
export { useBreakpoint as useComposition };

/**
 * What the browser can do, as opposed to what shape it is — touch, hover, reduced motion,
 * backdrop-filter support. Kept separate from the composition on purpose; see `deviceProfile.js`.
 */
export function useDeviceProfile() {
  return useSyncExternalStore(
    subscribeDeviceProfile, getDeviceProfileSnapshot, getDeviceProfileServerSnapshot,
  );
}

/**
 * The crop-aware content box of the reel video, for the given authored frame size.
 *
 * Measured from the viewport rather than a container ref on purpose: the Projectionist's container
 * and the OverlayPortal are both `position: fixed; inset: 0`, and the <video> fills its container
 * — so all three are exactly the viewport. Using the layout-viewport dimensions avoids threading a
 * ref across the portal boundary for a value that is definitionally the same.
 *
 * The object-fit is resolved HERE, from the same `reelVideoFit(breakpoint)` the Projectionist
 * styles its surfaces with, rather than taken as an argument. An anchor map that disagreed with
 * the fit the browser actually applied would put every point tag, product card and collection
 * pill in the wrong place, and a shared derivation is the only way that cannot drift — no call
 * site can forget to pass it. Desktop resolves `cover`, exactly as before.
 */
export function useVideoBox(nativeWidth, nativeHeight) {
  const { width, height } = useViewportSize();
  const breakpoint = useBreakpoint();
  const fit = reelVideoFit(breakpoint);
  return useMemo(
    () => computeVideoBox(width, height, nativeWidth, nativeHeight, fit),
    [width, height, nativeWidth, nativeHeight, fit],
  );
}
